import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Tag,
  Space,
  Statistic,
  Row,
  Col,
  Popconfirm,
  message,
  Badge,
  Modal,
  Switch
} from 'antd';
import {
  ReloadOutlined,
  SearchOutlined,
  UserOutlined,
  ClockCircleOutlined,
  LaptopOutlined,
  LogoutOutlined,
  DeleteOutlined,
  WarningOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import { makeGetCall, makePostCall } from '../../utils/helper';
import ErrorBoundary from '../../components/ErrorBoundary';
import './session-management.css';

const { Search } = Input;
const { Option } = Select;

const SessionManagement = () => {
  const [sessions, setSessions] = useState({ data: [], loading: false, error: null });
  // const [stats, setStats] = useState({ data: null, loading: false });
  const [searchText, setSearchText] = useState('');
  const [selectedUserType, setSelectedUserType] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(null);

  // Fetch active sessions
  const fetchSessions = useCallback(async () => {
    setSessions(prev => ({ ...prev, loading: true, error: null }));
    try {
      const response = await makeGetCall('/admin/sessions/active');
      setSessions({
        data: response.data?.sessions || [],
        loading: false,
        error: null
      });
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to load sessions';
      setSessions({ data: [], loading: false, error: errorMessage });
      message.error(errorMessage);
    }
  }, []);

  // Fetch session statistics
  // const fetchStats = useCallback(async () => {
  //   setStats(prev => ({ ...prev, loading: true }));
  //   try {
  //     const response = await makeGetCall('/admin/sessions/stats');
  //     setStats({
  //       data: response.data?.stats || null,
  //       loading: false
  //     });
  //   } catch (error) {
  //     console.error('Failed to load stats:', error);
  //     setStats({ data: null, loading: false });
  //   }
  // }, []);

  // Logout specific session
  const handleLogoutSession = async (sessionId) => {
    try {
      await makePostCall(`/admin/sessions/logout/${sessionId}`, {});
      message.success('Session terminated successfully');
      fetchSessions();
      // fetchStats();
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to logout session');
    }
  };

  // Logout all sessions for a user
  const handleLogoutUser = async (userId, username) => {
    try {
      const response = await makePostCall(`/admin/sessions/logout-user/${userId}`, {});
      message.success(`All sessions terminated for ${username} (${response.data?.sessions_terminated || 0} sessions)`);
      fetchSessions();
      // fetchStats();
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to logout user sessions');
    }
  };

  // Manual cleanup
  const handleManualCleanup = async () => {
    try {
      const response = await makePostCall('/admin/sessions/cleanup', {});
      message.success(`Cleanup completed: ${response.data?.total_deleted || 0} sessions deleted`);
      fetchSessions();
      // fetchStats();
    } catch (error) {
      message.error(error.response?.data?.message || 'Cleanup failed');
    }
  };

  // Format duration
  const formatDuration = (minutes) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours < 24) return `${hours}h ${mins}m`;
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return `${days}d ${remainingHours}h`;
  };

  // Format time ago
  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now - time;
    const diffMins = Math.floor(diffMs / (1000 * 60));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  // Filtered sessions
  const filteredSessions = useMemo(() => {
    let filtered = sessions.data;

    // Search filter
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(session =>
        session.username?.toLowerCase().includes(searchLower) ||
        session.user_fullname?.toLowerCase().includes(searchLower) ||
        session.ip_address?.includes(searchLower)
      );
    }

    // User type filter
    if (selectedUserType) {
      filtered = filtered.filter(session => session.user_type === selectedUserType);
    }

    return filtered;
  }, [sessions.data, searchText, selectedUserType]);

  // Unique user types for filter
  const userTypes = useMemo(() => {
    const types = new Set(sessions.data.map(s => s.user_type).filter(Boolean));
    return Array.from(types).sort();
  }, [sessions.data]);

  // Table columns
  const columns = [
    {
      title: 'User',
      key: 'user',
      width: 250,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            {record.user_fullname || record.username}
          </div>
          <div style={{ fontSize: 12, color: '#888' }}>
            <UserOutlined style={{ marginRight: 4 }} />
            {record.username}
          </div>
          {record.user_type && (
            <Tag color="blue" style={{ marginTop: 4, fontSize: 11 }}>
              {record.user_type}
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Session Info',
      key: 'session',
      width: 300,
      render: (_, record) => (
        <div>
          <div style={{ marginBottom: 4, fontSize: 12 }}>
            <LaptopOutlined style={{ marginRight: 6, color: '#1890ff' }} />
            <span style={{ color: '#666' }}>
              {record.user_agent || 'Unknown Device'}
            </span>
          </div>
          <div style={{ fontSize: 12 }}>
            <span style={{ color: '#888' }}>IP:</span>{' '}
            <Tag style={{ fontSize: 11 }}>{record.ip_address || 'Unknown'}</Tag>
          </div>
        </div>
      ),
    },
    {
      title: 'Activity',
      key: 'activity',
      width: 200,
      render: (_, record) => (
        <div>
          <div style={{ marginBottom: 4, fontSize: 12 }}>
            <ClockCircleOutlined style={{ marginRight: 6, color: '#52c41a' }} />
            <span style={{ color: '#666' }}>
              Login: {formatTimeAgo(record.login_time)}
            </span>
          </div>
          <div style={{ fontSize: 12 }}>
            <ClockCircleOutlined style={{ marginRight: 6, color: '#722ed1' }} />
            <span style={{ color: '#666' }}>
              Active: {formatTimeAgo(record.last_activity)}
            </span>
          </div>
          <div style={{ marginTop: 4 }}>
            <Badge
              status={record.duration_minutes < 5 ? 'success' : record.duration_minutes < 60 ? 'processing' : 'default'}
              text={
                <span style={{ fontSize: 11, color: '#888' }}>
                  Duration: {formatDuration(record.duration_minutes)}
                </span>
              }
            />
          </div>
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Popconfirm
            title="Logout Session"
            description={`Terminate this session for ${record.username}?`}
            onConfirm={() => handleLogoutSession(record.session_id)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<LogoutOutlined />}
            >
              Logout
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Initial load
  useEffect(() => {
    fetchSessions();
    // fetchStats();
  }, [fetchSessions]);

  // Auto-refresh
  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        fetchSessions();
        // fetchStats();
      }, 30000); // Refresh every 30 seconds
      setRefreshInterval(interval);
      return () => clearInterval(interval);
    } else {
      if (refreshInterval) {
        clearInterval(refreshInterval);
        setRefreshInterval(null);
      }
    }
  }, [autoRefresh, fetchSessions]);

  return (
    <ErrorBoundary>
      <div className="session-management-container">
        <div className="session-management-header">
          <h1>Session Management</h1>
          <p>Monitor and manage active user sessions</p>
        </div>

        {/* Statistics Cards */}
        {/* <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Active Sessions"
                value={stats.data?.total_active_sessions || 0}
                prefix={<UserOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Active Last Hour"
                value={stats.data?.active_last_hour || 0}
                prefix={<ClockCircleOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Active Last 24h"
                value={stats.data?.active_last_24_hours || 0}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Inactive"
                value={stats.data?.total_inactive_sessions || 0}
                prefix={<WarningOutlined />}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
        </Row> */}

        {/* Filters and Actions */}
        <Card style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={12} md={8}>
              <Search
                placeholder="Search by username, name, or IP"
                allowClear
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                prefix={<SearchOutlined />}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Filter by user type"
                allowClear
                style={{ width: '100%' }}
                value={selectedUserType}
                onChange={setSelectedUserType}
              >
                {userTypes.map(type => (
                  <Option key={type} value={type}>{type}</Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} md={10} style={{ textAlign: 'right' }}>
              <Space wrap>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: '14px', color: '#262626' }}>Auto Sync</span>
                  <Switch
                    checked={autoRefresh}
                    onChange={setAutoRefresh}
                    checkedChildren="ON"
                    unCheckedChildren="OFF"
                  />
                </div>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    fetchSessions();
                    // fetchStats();
                  }}
                  loading={sessions.loading}
                >
                  Refresh
                </Button>
                <Popconfirm
                  title="Manual Cleanup"
                  description="Delete old inactive sessions and stale active sessions?"
                  onConfirm={handleManualCleanup}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button
                    icon={<DeleteOutlined />}
                    danger
                  >
                    Cleanup
                  </Button>
                </Popconfirm>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Sessions Table */}
        <Card>
          <Table
            columns={columns}
            dataSource={filteredSessions}
            rowKey="session_id"
            loading={sessions.loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} session(s)`,
            }}
            scroll={{ x: 900 }}
            locale={{
              emptyText: sessions.error ? (
                <div style={{ padding: 20 }}>
                  <WarningOutlined style={{ fontSize: 24, color: '#ff4d4f', marginBottom: 8 }} />
                  <div>{sessions.error}</div>
                </div>
              ) : (
                'No active sessions'
              )
            }}
          />
        </Card>
      </div>
    </ErrorBoundary>
  );
};

export default SessionManagement;
